/**
 * Shopify Infinite Scroll for Collection Pages
 * Automatically loads more products as the user scrolls down
 */

class InfiniteScroll {
  constructor(options = {}) {
    // Configuration
    this.productGridSelector = options.productGridSelector || '.collection .grid, .collection-grid, [data-collection-products]';
    this.productItemSelector = options.productItemSelector || '.grid__item, .product-item, [data-product-item]';
    this.paginationSelector = options.paginationSelector || '.pagination, [data-pagination]';
    this.nextLinkSelector = options.nextLinkSelector || '.pagination__item--next a, .pagination a[rel="next"], a[aria-label="Next page"]';
    this.loadingOffset = options.loadingOffset || 300; // pixels from bottom to trigger load
    
    // State
    this.isLoading = false;
    this.hasMorePages = true;
    this.currentPage = 1;
    this.nextPageUrl = null;
    
    // DOM elements
    this.productGrid = null;
    this.pagination = null;
    this.loadingIndicator = null;
    
    this.init();
  }
  
  init() {
    // Find the product grid
    this.productGrid = document.querySelector(this.productGridSelector);
    if (!this.productGrid) {
      console.warn('Infinite Scroll: Product grid not found');
      return;
    }
    
    // Find pagination
    this.pagination = document.querySelector(this.paginationSelector);
    if (!this.pagination) {
      console.warn('Infinite Scroll: Pagination not found');
      return;
    }
    
    // Get initial next page URL
    this.updateNextPageUrl();
    
    // Hide original pagination
    this.pagination.style.display = 'none';
    
    // Create loading indicator
    this.createLoadingIndicator();
    
    // Set up scroll listener
    this.setupScrollListener();
    
    console.log('Infinite Scroll: Initialized');
  }
  
  updateNextPageUrl() {
    const nextLink = document.querySelector(this.nextLinkSelector);
    if (nextLink && nextLink.href) {
      this.nextPageUrl = nextLink.href;
      this.hasMorePages = true;
    } else {
      this.hasMorePages = false;
      this.nextPageUrl = null;
    }
  }
  
  createLoadingIndicator() {
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.className = 'infinite-scroll-loading';
    this.loadingIndicator.innerHTML = `
      <div class="infinite-scroll-spinner">
        <div class="spinner-border"></div>
        <p>Loading more products...</p>
      </div>
    `;
    this.loadingIndicator.style.display = 'none';
    
    // Insert after product grid
    this.productGrid.parentNode.insertBefore(
      this.loadingIndicator,
      this.productGrid.nextSibling
    );
    
    // Add CSS
    this.injectStyles();
  }
  
  injectStyles() {
    if (document.getElementById('infinite-scroll-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'infinite-scroll-styles';
    style.textContent = `
      .infinite-scroll-loading {
        text-align: center;
        padding: 40px 20px;
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      
      .infinite-scroll-loading.active {
        opacity: 1;
      }
      
      .infinite-scroll-spinner {
        display: inline-block;
      }
      
      .spinner-border {
        width: 40px;
        height: 40px;
        border: 4px solid rgba(0, 0, 0, 0.1);
        border-left-color: #000;
        border-radius: 50%;
        animation: infinite-scroll-spin 0.8s linear infinite;
        margin: 0 auto 15px;
      }
      
      @keyframes infinite-scroll-spin {
        to { transform: rotate(360deg); }
      }
      
      .infinite-scroll-loading p {
        margin: 0;
        font-size: 14px;
        color: #666;
      }
      
      /* Smooth appearance for new products */
      .infinite-scroll-item {
        opacity: 0;
        animation: infinite-scroll-fade-in 0.3s ease forwards;
      }
      
      @keyframes infinite-scroll-fade-in {
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }
  
  setupScrollListener() {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }
  
  handleScroll() {
    if (this.isLoading || !this.hasMorePages) return;
    
    const scrollPosition = window.scrollY + window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const triggerPoint = documentHeight - this.loadingOffset;
    
    if (scrollPosition >= triggerPoint) {
      this.loadMoreProducts();
    }
  }
  
  async loadMoreProducts() {
    if (this.isLoading || !this.hasMorePages || !this.nextPageUrl) return;
    
    this.isLoading = true;
    this.showLoading();
    
    try {
      const response = await fetch(this.nextPageUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract new products
      const newProducts = doc.querySelectorAll(this.productItemSelector);
      
      if (newProducts.length === 0) {
        this.hasMorePages = false;
        this.hideLoading();
        this.showEndMessage();
        return;
      }
      
      // Add new products to grid
      newProducts.forEach((product, index) => {
        const clonedProduct = product.cloneNode(true);
        clonedProduct.classList.add('infinite-scroll-item');
        clonedProduct.style.animationDelay = `${index * 0.02}s`; // Faster stagger
        this.productGrid.appendChild(clonedProduct);
        
        // Execute any inline scripts within the product
        const scripts = clonedProduct.querySelectorAll('script');
        scripts.forEach(oldScript => {
          const newScript = document.createElement('script');
          Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value);
          });
          newScript.textContent = oldScript.textContent;
          oldScript.parentNode.replaceChild(newScript, oldScript);
        });
      });
      
      // Update current page
      this.currentPage++;
      
      // Update next page URL from the fetched document
      const newPagination = doc.querySelector(this.paginationSelector);
      if (newPagination) {
        const oldPagination = this.pagination;
        this.pagination = newPagination;
        this.pagination.style.display = 'none';
        oldPagination.parentNode.replaceChild(this.pagination, oldPagination);
        this.updateNextPageUrl();
      } else {
        this.hasMorePages = false;
      }
      
      // Trigger Shopify theme events for any scripts that need to reinitialize
      this.triggerShopifyEvents();
      
      console.log(`Infinite Scroll: Loaded page ${this.currentPage}`);
      
    } catch (error) {
      console.error('Infinite Scroll: Error loading products', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }
  
  showLoading() {
    // Only show loading indicator if products aren't loaded quickly
    this.loadingTimeout = setTimeout(() => {
      this.loadingIndicator.style.display = 'block';
      // Trigger reflow for animation
      void this.loadingIndicator.offsetWidth;
      this.loadingIndicator.classList.add('active');
    }, 200); // Only show if loading takes more than 200ms
  }
  
  hideLoading() {
    clearTimeout(this.loadingTimeout);
    this.loadingIndicator.classList.remove('active');
    setTimeout(() => {
      this.loadingIndicator.style.display = 'none';
    }, 300);
  }
  
  showError() {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'infinite-scroll-error';
    errorMessage.style.cssText = 'text-align: center; padding: 20px; color: #d32f2f;';
    errorMessage.innerHTML = `
      <p>Sorry, we couldn't load more products. Please try again.</p>
      <button onclick="location.reload()" style="padding: 10px 20px; margin-top: 10px; cursor: pointer;">Reload Page</button>
    `;
    this.loadingIndicator.parentNode.insertBefore(
      errorMessage,
      this.loadingIndicator.nextSibling
    );
  }
  
  triggerShopifyEvents() {
    // Dispatch custom event for theme scripts to listen to
    const event = new CustomEvent('infinitescroll:loaded', {
      detail: {
        page: this.currentPage,
        productsLoaded: true
      }
    });
    document.dispatchEvent(event);
    
    // Dawn Theme specific reinitialization
    // Reinitialize quick add buttons
    if (typeof window.Shopify !== 'undefined' && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
        },
      ]);
    }
    
    // Reinitialize product cards
    const newProductCards = this.productGrid.querySelectorAll('.card-wrapper:not([data-initialized])');
    newProductCards.forEach(card => {
      card.setAttribute('data-initialized', 'true');
      
      // Trigger any product card specific events
      const productCard = card.querySelector('.card');
      if (productCard) {
        productCard.dispatchEvent(new Event('product-card:initialized'));
      }
    });
    
    // Reinitialize any media elements (videos, 3D models)
    const mediaElements = this.productGrid.querySelectorAll('deferred-media, product-modal');
    mediaElements.forEach(element => {
      if (element.loadContent) {
        element.loadContent();
      }
    });
    
    // Trigger common Shopify theme reinitialization
    if (typeof Shopify !== 'undefined' && Shopify.ProductReviews) {
      Shopify.ProductReviews.refresh();
    }
    
    // Currency converter support
    if (typeof Currency !== 'undefined' && Currency.convertAll) {
      Currency.convertAll(shopCurrency, jQuery('[name=currencies]').val());
    }
    
    // Reinitialize quick view if present
    if (typeof theme !== 'undefined' && theme.reinitProductGridItem) {
      theme.reinitProductGridItem();
    }
    
    // Dawn theme: Reinitialize Shopify section rendering
    if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
      // In theme editor
      document.dispatchEvent(new CustomEvent('shopify:section:load'));
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);
} else {
  initInfiniteScroll();
}

function initInfiniteScroll() {
  // Only initialize on collection pages
  // Check for various collection page indicators
  const isCollectionPage = 
    document.body.classList.contains('template-collection') || 
    document.querySelector('[data-section-type="collection-template"]') ||
    document.querySelector('#ProductGridContainer') ||
    document.querySelector('.collection') ||
    window.location.pathname.includes('/collections/');
  
  if (isCollectionPage) {
    // Dawn Theme Configuration
    new InfiniteScroll({
      productGridSelector: '#product-grid, #ProductGridContainer ul',
      productItemSelector: 'li.grid__item',
      paginationSelector: '.pagination-wrapper, .pagination',
      nextLinkSelector: '.pagination__item--next a, a[aria-label*="Next"]',
      loadingOffset: 1500 // Start loading well before reaching the bottom for seamless experience
    });
  }
}